import { lt } from './localized'
import { createStarterSkills } from './skills'
import type {
  Expedition,
  LocationExplorationState,
  LocalizedText,
  Location,
  LocationId,
  QuestRuntimeState,
  Region,
  RegionId,
} from './types'

/** Diary pool entries NL / EN */
function diary(entries: [string, string][]): LocalizedText[] {
  return entries.map(([nl, en]) => lt(nl, en))
}

const LOOT_REVEAL_AT_PCT = 20
export const LOCATION_UNLOCK_EXPLORATION_PCT = 70

const THOMAS_RECRUIT = {
  id: 'thomas',
  name: 'Thomas',
  role: 'Medic',
  status: 'Idle',
  gender: 'male',
  portrait: 'male_02',
  needs: { hunger: 100, thirst: 100, energy: 100 },
  skills: { ...createStarterSkills({ stealth: 4 }), first_aid: { level: 2, xp: 0, unlocked: true } },
  loadout: { weapon: null, tool: null },
  bio: lt(
    'Halverwege zijn studie geneeskunde toen het misging. Praat zacht, snijdt zekerder.',
    'Halfway through medical school when everything fell apart. Speaks softly, cuts steadier.',
  ),
} as const

const BART_RECRUIT = {
  id: 'bart',
  name: 'Bart',
  role: 'Scavenger',
  status: 'Idle',
  gender: 'male',
  portrait: 'male_04',
  needs: { hunger: 100, thirst: 100, energy: 100 },
  skills: { ...createStarterSkills({ endurance: 2 }), navigation: { level: 3, xp: 0, unlocked: true } },
  loadout: { weapon: null, tool: null },
  bio: lt(
    'Weigerde zijn eigen dorp te verlaten. Nu is er niet veel meer van over.',
    'Refused to leave his hometown. Now there isn\'t much left of it.',
  ),
} as const

const LAURA_RECRUIT = {
  id: 'laura',
  name: 'Laura',
  role: 'Cook',
  status: 'Idle',
  gender: 'female',
  portrait: 'female_02',
  needs: { hunger: 100, thirst: 100, energy: 100 },
  skills: { ...createStarterSkills({ endurance: 2 }), cooking: { level: 2, xp: 0, unlocked: true } },
  loadout: { weapon: null, tool: null },
  bio: lt(
    'Hield ooit een gaarkeuken open in Berlicum. Tilt jerrycans alsof het lucht is.',
    'Once ran a soup kitchen in Berlicum. Lifts jerrycans like they weigh nothing.',
  ),
} as const

const WILLEM_RECRUIT = {
  id: 'willem',
  name: 'Willem',
  role: 'Scavenger',
  status: 'Idle',
  gender: 'male',
  portrait: 'male_02',
  needs: { hunger: 100, thirst: 100, energy: 100 },
  skills: { ...createStarterSkills(), climbing: { level: 2, xp: 0, unlocked: true } },
  loadout: { weapon: null, tool: null },
  bio: lt(
    'Klom al als kind overal overheen. Nu doet hij het voor overleving.',
    'Climbed everything as a kid. Now he does it to survive.',
  ),
} as const

const TAMARA_RECRUIT = {
  id: 'tamara',
  name: 'Tamara',
  role: 'Scavenger',
  status: 'Idle',
  gender: 'female',
  portrait: 'female_03',
  needs: { hunger: 100, thirst: 100, energy: 100 },
  skills: { ...createStarterSkills(), driving: { level: 2, xp: 0, unlocked: true }, navigation: { level: 3, xp: 0, unlocked: true } },
  loadout: { weapon: null, tool: null },
  bio: lt(
    'Reed vroeger een bestelbus door heel Nederland. Weet nog elke route.',
    'Used to drive a van all over the Netherlands. Still knows every route.',
  ),
} as const

const LOTTE_TRADER_RECRUIT = {
  id: 'lotte-trader',
  name: 'Lotte',
  role: 'Trader' as const,
  status: 'Idle',
  gender: 'female',
  portrait: 'female_02',
  needs: { hunger: 100, thirst: 100, energy: 100 },
  skills: {
    ...createStarterSkills({ endurance: 2 }),
    bartering: { level: 3, xp: 0, unlocked: true },
    persuasion: { level: 2, xp: 0, unlocked: true },
  },
  loadout: { weapon: null, tool: null },
  bio: lt(
    'Handelde vroeger op markten. Nu ruilt ze voor kilometers en geloofwaardigheid.',
    'Used to trade at markets. Now she swaps distance for credibility.',
  ),
} as const

export const REGIONS: Record<RegionId, Region> = {
  'rural-south': {
    id: 'rural-south',
    name: lt(
      'Rurale Zuid',
      'Rural South',
    ),
    blurb: lt(
      'Akkers, weilanden, leeggelopen dorpen ten zuiden van de Maas.',
      'Fields, meadows, emptied villages south of the Maas.',
    ),
    unlockAt: 0,
  },
  'den-bosch': {
    id: 'den-bosch',
    name: lt("'s-Hertogenbosch", "'s-Hertogenbosch"),
    blurb: lt(
      'De stad ligt stil. Hoge gebouwen, lange straten, oude geheimen.',
      'The city lies still. Tall buildings, long streets, old secrets.',
    ),
    unlockAt: 8,
  },
  'a2-highway': {
    id: 'a2-highway',
    name: lt('A2 corridor', 'A2 corridor'),
    blurb: lt(
      'Wrakken en rustplaatsen langs de snelweg naar het noorden.',
      'Wrecks and rest stops along the highway north.',
    ),
    unlockAt: 0,
  },
  utrecht: {
    id: 'utrecht',
    name: lt('Utrecht', 'Utrecht'),
    blurb: lt(
      'Grachten, flatgebieden en oude universiteit — een stad om te verkennen.',
      'Canals, towers, and old university wings — a city to unravel.',
    ),
    unlockAt: 0,
  },
}

export const REGION_LIST: Region[] = [
  REGIONS['rural-south'],
  REGIONS['den-bosch'],
  REGIONS['a2-highway'],
  REGIONS.utrecht,
]

export const LOCATIONS: Record<LocationId, Location> = {
  'vinkel-bridge': {
    id: 'vinkel-bridge',
    region: 'rural-south',
    name: lt('Ingang naar het dorp', 'Village Outskirts'),
    blurb: lt(
      'De brug staat nog. De auto erop al twee jaar niet meer.',
      'The bridge still stands. The car on it has not moved for two years.',
    ),
    durationMs: 90 * 1000,
    injuryLevel: { minDurationMs: 1 * 60 * 1000, maxDurationMs: 3 * 60 * 1000 },
    cost: { hunger: 35, thirst: 30, energy: 5 },
    rolls: { min: 1, max: 3 },
    loot: [
      { itemId: 'scrap-rusty', weight: 6, min: 1, max: 4 },
      { itemId: 'scrap-plank', weight: 4, min: 1, max: 2 },
      { itemId: 'food-bread', weight: 3, min: 1, max: 1 },
      { itemId: 'water-bottle-half', weight: 2, min: 1, max: 2 },
      { itemId: 'scrap-wire', weight: 2, min: 1, max: 2 },
      { itemId: 'water-rain-jerrycan', weight: 1, min: 1, max: 1 },
      { itemId: 'food-crackers', weight: 1, min: 1, max: 1 },
      { itemId: 'trinket-lighter', weight: 1, min: 1, max: 1 },
      { itemId: 'weapon-pipe', weight: 2, min: 1, max: 1 },
      { itemId: 'trinket-soccer-ball', weight: 1, min: 1, max: 1 },
    ],
    diary: diary([
      [
        'Het asfalt kraakt. Hier en daar groeit gras door de scheuren.',
        'The asphalt cracks. Grass pushes through here and there.',
      ],
      [
        'Een verroeste fiets ligt half in de sloot. De bel doet het nog.',
        'A rusted bike lies half in the ditch. The bell still works.',
      ],
      [
        'Ik kwam een hond tegen. Hij keek me aan en liep weg.',
        'I met a dog. It looked at me and walked away.',
      ],
      [
        'Een briefje aan een lantaarnpaal: "Wij zijn naar Berlicum gegaan." Geen datum.',
        'A note on a lamppost: “We went to Berlicum.” No date.',
      ],
      [
        'De wind staat verkeerd. Ik ruik iets onaangenaams. Doorlopen.',
        'The wind is wrong. Something foul—keep walking.',
      ],
      [
        'Eindelijk drinkbaar water gevonden. Of het smaakt zo.',
        'Finally, drinkable water. Or it tastes like it.',
      ],
    ]),
    exploration: {
      progressGain: { min: 12, max: 18 },
      lootRevealAtPct: LOOT_REVEAL_AT_PCT,
      tierRewards: [
        { thresholdPct: 20, guaranteedLoot: [{ defId: 'food-bread', quantity: 1 }] },
        { thresholdPct: 50, guaranteedLoot: [{ defId: 'water-rain-jerrycan', quantity: 1 }] },
        { thresholdPct: 70, guaranteedLoot: [{ defId: 'scrap-rusty', quantity: 2 }] },
        { thresholdPct: 100, guaranteedLoot: [{ defId: 'trinket-rubiks', quantity: 1 }] },
      ],
      recruit: {
        atPct: 50,
        survivor: THOMAS_RECRUIT,
      },
    },
  },
  'rural-supermarket': {
    id: 'rural-supermarket',
    region: 'rural-south',
    name: lt('Dorpssupermarkt', 'Supermarket'),
    blurb: lt(
      'De automatische deuren staan open. Iemand heeft ze opengewrikt.',
      'Automatic doors stand open. Someone wedged them that way.',
    ),
    durationMs: 2 * 60 * 1000,
    injuryLevel: { minDurationMs: 2 * 60 * 1000, maxDurationMs: 5 * 60 * 1000 },
    cost: { hunger: 50, thirst: 40, energy: 10 },
    rolls: { min: 2, max: 3 },
    loot: [
      { itemId: 'food-canned-meatballs', weight: 4, min: 1, max: 2 },
      { itemId: 'food-bread', weight: 4, min: 1, max: 1 },
      { itemId: 'food-crackers', weight: 4, min: 1, max: 2 },
      { itemId: 'meds-bandages', weight: 2, min: 1, max: 1 },
      { itemId: 'scrap-rusty', weight: 3, min: 1, max: 3 },
      { itemId: 'scrap-plank', weight: 3, min: 1, max: 2 },
      { itemId: 'water-bottle-half', weight: 4, min: 1, max: 2 },
      { itemId: 'meds-paracetamol', weight: 2, min: 1, max: 1 },
      { itemId: 'scrap-wire', weight: 2, min: 1, max: 1 },
      { itemId: 'food-stamppot', weight: 1, min: 1, max: 1 },
      { itemId: 'weapon-plank', weight: 1, min: 1, max: 1 },
      { itemId: 'weapon-tennis-racket', weight: 1, min: 1, max: 1 },
      { itemId: 'trinket-rubiks', weight: 1, min: 1, max: 1 },
    ],
    diary: diary([
      [
        'De diepvrieskasten zijn al lang ontdooid. De stank trekt door alles heen.',
        'Freezers thawed long ago. The smell stains everything.',
      ],
      [
        'Achter het kassa-gordijn vond ik een dagboek. Ik liet het liggen.',
        'Behind the cashier curtain, a diary. I left it.',
      ],
      [
        'Een kar staat vol blikken. Iemand stopte halverwege.',
        'A cart full of cans—stopped halfway.',
      ],
      [
        'Dat was op het nippertje — iets bewoog tussen de schappen.',
        'Close call—something moved between the aisles.',
      ],
      [
        'In het magazijn kwam ik een prijskaartje tegen: €2,49. Dat was het.',
        'In the stockroom a price tag: €2,49. That was then.',
      ],
      [
        'Een poster: "Bonus deze week — appels". De appels zijn er niet meer.',
        'A poster: “Bonus this week — apples.” The apples are gone.',
      ],
    ]),
    unlockRequirement: {
      locationId: 'vinkel-bridge',
      explorationPct: LOCATION_UNLOCK_EXPLORATION_PCT,
    },
    exploration: {
      progressGain: { min: 10, max: 15 },
      lootRevealAtPct: LOOT_REVEAL_AT_PCT,
      tierRewards: [
        {
          thresholdPct: 20,
          guaranteedLoot: [{ defId: 'food-canned-meatballs', quantity: 1 }],
        },
        {
          thresholdPct: 35,
          guaranteedLoot: [{ defId: 'meds-bandages', quantity: 1 }],
        },
        { thresholdPct: 50, guaranteedLoot: [{ defId: 'meds-paracetamol', quantity: 1 }] },
        { thresholdPct: 70, guaranteedLoot: [{ defId: 'food-stamppot', quantity: 1 }] },
        { thresholdPct: 100, guaranteedLoot: [{ defId: 'trinket-soccer-ball', quantity: 1 }] },
      ],
    },
  },
  'cafe-pannekoek': {
    id: 'cafe-pannekoek',
    region: 'rural-south',
    name: lt('Café Den Driehoek', 'Café Den Driehoek'),
    blurb: lt(
      'Een dorpscafé waar het bier op was. De keuken minder dan je denkt.',
      'A village café long out of beer. But the kitchen has more than you think.',
    ),
    durationMs: 110 * 1000,
    injuryLevel: { minDurationMs: 2 * 60 * 1000, maxDurationMs: 4 * 60 * 1000 },
    cost: { hunger: 48, thirst: 42, energy: 12 },
    rolls: { min: 2, max: 3 },
    loot: [
      { itemId: 'food-bread', weight: 5, min: 1, max: 2 },
      { itemId: 'scrap-rusty', weight: 4, min: 1, max: 3 },
      { itemId: 'food-crackers', weight: 3, min: 1, max: 2 },
      { itemId: 'food-stamppot', weight: 3, min: 1, max: 1 },
      { itemId: 'scrap-plank', weight: 2, min: 1, max: 2 },
      { itemId: 'water-bottle-half', weight: 2, min: 1, max: 1 },
      { itemId: 'trinket-lighter', weight: 2, min: 1, max: 1 },
      { itemId: 'scrap-wire', weight: 1, min: 1, max: 1 },
      { itemId: 'food-chocolate', weight: 1, min: 1, max: 1 },
      { itemId: 'trinket-vinyl', weight: 1, min: 1, max: 1 },
      { itemId: 'book-home-cooking', weight: 1, min: 1, max: 1 },
      { itemId: 'tool-cooking-pot', weight: 1, min: 1, max: 1 },
    ],
    diary: diary([
      [
        'Aan de bar staan nog stoelen recht. Iemand hield het hier netjes.',
        'Chairs still upright at the bar. Someone kept it tidy.',
      ],
      [
        'In de jukebox zat één plaat. "Doe Maar". Ik draaide een kant.',
        'One record in the jukebox. Doe Maar. I played one side.',
      ],
      [
        'Achter de bar vond ik een halfvolle pan stamppot. Koud, maar heel.',
        'Behind the bar, half a pot of stamppot—cold but intact.',
      ],
      [
        'Op een biertje na is hier alles weg. Wel rustig.',
        'Aside from one beer, everything is gone. Quiet, though.',
      ],
      [
        'Iemand schreef "BLIJF UIT DE KELDER" op de wc-deur. Ik bleef uit de kelder.',
        'Someone wrote STAY OUT OF THE CELLAR on the toilet door. I stayed out.',
      ],
    ]),
    unlockRequirement: {
      locationId: 'rural-supermarket',
      explorationPct: LOCATION_UNLOCK_EXPLORATION_PCT,
    },
    exploration: {
      progressGain: { min: 10, max: 14 },
      lootRevealAtPct: LOOT_REVEAL_AT_PCT,
      tierRewards: [
        { thresholdPct: 20, guaranteedLoot: [{ defId: 'food-bread', quantity: 1 }] },
        { thresholdPct: 50, guaranteedLoot: [{ defId: 'food-chocolate', quantity: 1 }] },
        { thresholdPct: 70, guaranteedLoot: [{ defId: 'food-stamppot', quantity: 1 }] },
        {
          thresholdPct: 100,
          guaranteedLoot: [
            { defId: 'trinket-vinyl', quantity: 1 },
            { defId: 'book-home-cooking', quantity: 1 },
          ],
        },
      ],
      recruit: {
        atPct: 50,
        survivor: BART_RECRUIT,
      },
    },
  },
  'abandoned-farm': {
    id: 'abandoned-farm',
    region: 'rural-south',
    name: lt('Verlaten boerderij', 'Abandoned farm'),
    blurb: lt(
      'De staldeur klappert. Niemand om hem dicht te doen.',
      'The barn door bangs. Nobody left to close it.',
    ),
    durationMs: 2 * 60 * 1000,
    injuryLevel: { minDurationMs: 3 * 60 * 1000, maxDurationMs: 6 * 60 * 1000 },
    cost: { hunger: 55, thirst: 45, energy: 15 },
    rolls: { min: 2, max: 3 },
    loot: [
      { itemId: 'water-rain-jerrycan', weight: 5, min: 1, max: 2 },
      { itemId: 'scrap-rusty', weight: 4, min: 1, max: 2 },
      { itemId: 'scrap-plank', weight: 4, min: 1, max: 2 },
      { itemId: 'food-canned-meatballs', weight: 3, min: 1, max: 1 },
      { itemId: 'meds-paracetamol', weight: 2, min: 1, max: 1 },
      { itemId: 'scrap-toolkit', weight: 1, min: 1, max: 1 },
      { itemId: 'trinket-plush', weight: 1, min: 1, max: 1 },
      { itemId: 'book-archery-starter', weight: 1, min: 1, max: 1 },
      { itemId: 'weapon-bow', weight: 1, min: 1, max: 1 },
    ],
    diary: diary([
      [
        'De boerderij staat nog overeind. Het hek hangt schuin.',
        'The farm still stands. The gate hangs crooked.',
      ],
      [
        'In de stal liggen botten. Ik weet niet van wat.',
        'Bones in the barn. I do not know from what.',
      ],
      [
        'Onder een omgevallen kast vond ik een trommel met aspirine.',
        'Under a fallen cabinet, a tin of aspirin.',
      ],
      [
        'Het regende. Ik vulde drie jerrycans bij de pijp.',
        'It rained. I filled three jerrycans from the downpipe.',
      ],
      [
        'Iemands kinderkamer. Een knuffel olifant op het bed. Ik nam hem mee.',
        'Someone’s nursery. A plush elephant on the bed. I took it.',
      ],
    ]),
    unlockRequirement: {
      locationId: 'cafe-pannekoek',
      explorationPct: LOCATION_UNLOCK_EXPLORATION_PCT,
    },
    exploration: {
      progressGain: { min: 9, max: 13 },
      lootRevealAtPct: LOOT_REVEAL_AT_PCT,
      tierRewards: [
        { thresholdPct: 20, guaranteedLoot: [{ defId: 'water-rain-jerrycan', quantity: 1 }] },
        { thresholdPct: 50, guaranteedLoot: [{ defId: 'scrap-toolkit', quantity: 1 }] },
        { thresholdPct: 70, guaranteedLoot: [{ defId: 'meds-paracetamol', quantity: 1 }] },
        {
          thresholdPct: 100,
          guaranteedLoot: [
            { defId: 'trinket-plush', quantity: 1 },
            { defId: 'book-archery-starter', quantity: 1 },
          ],
        },
      ],
      recruit: {
        atPct: 50,
        survivor: LAURA_RECRUIT,
      },
    },
  },
  'verkade-factory': {
    id: 'verkade-factory',
    region: 'den-bosch',
    name: lt('Verkade-fabriek', 'Verkade factory'),
    blurb: lt(
      'Een ingestorte hal met geuren die er niet meer horen.',
      'A collapsed hall with smells that no longer belong.',
    ),
    durationMs: 3 * 60 * 1000,
    injuryLevel: { minDurationMs: 4 * 60 * 1000, maxDurationMs: 8 * 60 * 1000 },
    cost: { hunger: 60, thirst: 50, energy: 15 },
    rolls: { min: 2, max: 4 },
    loot: [
      { itemId: 'water-bottle-half', weight: 5, min: 1, max: 2 },
      { itemId: 'scrap-rusty', weight: 4, min: 1, max: 3 },
      { itemId: 'food-bread', weight: 3, min: 1, max: 1 },
      { itemId: 'water-rain-jerrycan', weight: 3, min: 1, max: 1 },
      { itemId: 'food-chocolate', weight: 3, min: 1, max: 1 },
      { itemId: 'food-canned-meatballs', weight: 3, min: 1, max: 1 },
      { itemId: 'scrap-electronics', weight: 3, min: 1, max: 2 },
      { itemId: 'scrap-plank', weight: 1, min: 1, max: 1 },
      { itemId: 'meds-bandages', weight: 1, min: 1, max: 1 },
      { itemId: 'book-field-first-aid', weight: 1, min: 1, max: 1 },
    ],
    diary: diary([
      [
        'De koekjeslijn staat stil. Op de band ligt nog deeg.',
        'The biscuit line is still. Dough still on the belt.',
      ],
      [
        'Ergens zoemt iets. Een aggregaat? Ik kan het niet vinden.',
        'Something hums. A generator? I cannot find it.',
      ],
      [
        'Ik liep langs een productiekantoor. Kalender staat op maart 2040.',
        'Past a production office—calendar stuck on March 2040.',
      ],
      [
        'Pallets met chocoladerepen. Half opgegeten door iets kleins.',
        'Pallets of chocolate bars—half eaten by something small.',
      ],
      [
        'Op het dak heb ik om me heen gekeken. De stad is groter dan ik dacht.',
        'On the roof I looked around—the city is bigger than I thought.',
      ],
    ]),
    unlockRequirement: {
      locationId: 'abandoned-farm',
      explorationPct: LOCATION_UNLOCK_EXPLORATION_PCT,
    },
    exploration: {
      progressGain: { min: 8, max: 12 },
      lootRevealAtPct: LOOT_REVEAL_AT_PCT,
      tierRewards: [
        { thresholdPct: 20, guaranteedLoot: [{ defId: 'food-chocolate', quantity: 1 }] },
        { thresholdPct: 50, guaranteedLoot: [{ defId: 'scrap-electronics', quantity: 1 }] },
        { thresholdPct: 70, guaranteedLoot: [{ defId: 'meds-bandages', quantity: 1 }] },
        { thresholdPct: 100, guaranteedLoot: [{ defId: 'book-field-first-aid', quantity: 1 }] },
      ],
      recruit: {
        atPct: 50,
        survivor: WILLEM_RECRUIT,
      },
    },
  },
  'town-hall-offices': {
    id: 'town-hall-offices',
    region: 'den-bosch',
    name: lt('Stadhuis-kantoren', 'Town hall offices'),
    blurb: lt(
      'Marmeren gangen, omgevallen kasten, overal papier.',
      'Marble corridors, toppled cabinets, paper everywhere.',
    ),
    durationMs: 3 * 60 * 1000,
    injuryLevel: { minDurationMs: 4 * 60 * 1000, maxDurationMs: 9 * 60 * 1000 },
    cost: { hunger: 50, thirst: 60, energy: 10 },
    rolls: { min: 2, max: 3 },
    loot: [
      { itemId: 'scrap-electronics', weight: 5, min: 1, max: 2 },
      { itemId: 'water-bottle-half', weight: 3, min: 1, max: 2 },
      { itemId: 'water-rain-jerrycan', weight: 3, min: 1, max: 1 },
      { itemId: 'meds-bandages', weight: 3, min: 1, max: 1 },
      { itemId: 'water-mineral-sealed', weight: 2, min: 1, max: 1 },
      { itemId: 'trinket-watch', weight: 2, min: 1, max: 1 },
      { itemId: 'scrap-plank', weight: 1, min: 1, max: 1 },
      { itemId: 'meds-antibiotics', weight: 1, min: 1, max: 1 },
      { itemId: 'trinket-coin', weight: 1, min: 1, max: 1 },
      { itemId: 'book-driving-theory', weight: 1, min: 1, max: 1 },
    ],
    diary: diary([
      [
        'In de raadszaal staan de stoelen nog netjes opgesteld. Niemand vergaderd.',
        'Council chamber chairs still aligned. Nobody meets.',
      ],
      [
        'Een ambtenaar liet zijn lunchbox staan. Een appel, totaal verschrompeld.',
        'A civil servant’s lunchbox left behind—a shrivelled apple.',
      ],
      [
        'In een lade vond ik een vergulde euro-munt. Ik nam hem mee.',
        'In a drawer a gilt euro coin. I took it.',
      ],
      [
        'Op de derde verdieping piepte iets. Ik ben niet gaan kijken.',
        'Something squeaked on the third floor. I did not look.',
      ],
      [
        'De koffieautomaat staat nog aan. Hoe kan dat?',
        'The coffee machine is still on. How?',
      ],
    ]),
    unlockRequirement: {
      locationId: 'verkade-factory',
      explorationPct: LOCATION_UNLOCK_EXPLORATION_PCT,
    },
    exploration: {
      progressGain: { min: 8, max: 12 },
      lootRevealAtPct: LOOT_REVEAL_AT_PCT,
      tierRewards: [
        { thresholdPct: 20, guaranteedLoot: [{ defId: 'scrap-electronics', quantity: 1 }] },
        { thresholdPct: 50, guaranteedLoot: [{ defId: 'water-mineral-sealed', quantity: 1 }] },
        { thresholdPct: 70, guaranteedLoot: [{ defId: 'meds-antibiotics', quantity: 1 }] },
        { thresholdPct: 100, guaranteedLoot: [{ defId: 'trinket-watch', quantity: 1 }] },
      ],
      recruit: {
        atPct: 50,
        survivor: TAMARA_RECRUIT,
      },
    },
  },
  'fashion-stores': {
    id: 'fashion-stores',
    region: 'den-bosch',
    name: lt('Modeketens', 'Fashion chains'),
    blurb: lt(
      'Etalages stuk. Mannequins op straat. Stof overal.',
      'Shop windows shattered. Mannequins in the street. Fabric everywhere.',
    ),
    durationMs: 2 * 60 * 1000,
    injuryLevel: { minDurationMs: 3 * 60 * 1000, maxDurationMs: 7 * 60 * 1000 },
    cost: { hunger: 45, thirst: 50, energy: 5 },
    rolls: { min: 2, max: 4 },
    loot: [
      { itemId: 'scrap-rusty', weight: 4, min: 1, max: 2 },
      { itemId: 'scrap-plank', weight: 2, min: 1, max: 2 },
      { itemId: 'trinket-plush', weight: 3, min: 1, max: 1 },
      { itemId: 'trinket-watch', weight: 2, min: 1, max: 1 },
      { itemId: 'food-chocolate', weight: 2, min: 1, max: 1 },
      { itemId: 'water-mineral-sealed', weight: 1, min: 1, max: 1 },
      { itemId: 'trinket-vinyl', weight: 1, min: 1, max: 1 },
      { itemId: 'book-swimming-fundamentals', weight: 1, min: 1, max: 1 },
    ],
    diary: diary([
      [
        'Een mannequin stond midden op de roltrap. Heeft me even doen schrikken.',
        'A mannequin mid-escalator—gave me a start.',
      ],
      [
        'In het pashok lag een dagboek. Ik scheurde één blad eruit. Voor mezelf.',
        'A diary in the fitting room. I tore one page—for myself.',
      ],
      [
        'Iemand had de etalagepoppen omgekleed. Nieuwe trends.',
        'Someone redressed the window models. New trends.',
      ],
      [
        'Op de eerste verdieping vond ik een doosje juwelen. Goedkoop spul, mooi spul.',
        'First floor: a box of jewellery—cheap, pretty.',
      ],
      [
        'Een glanzende pluche olifant lag op de toonbank. Alsof iemand hem net had neergelegd.',
        'A glossy plush elephant on the counter—as if just placed.',
      ],
    ]),
    unlockRequirement: {
      locationId: 'town-hall-offices',
      explorationPct: LOCATION_UNLOCK_EXPLORATION_PCT,
    },
    exploration: {
      progressGain: { min: 9, max: 13 },
      lootRevealAtPct: LOOT_REVEAL_AT_PCT,
      tierRewards: [
        { thresholdPct: 20, guaranteedLoot: [{ defId: 'trinket-watch', quantity: 1 }] },
        { thresholdPct: 50, guaranteedLoot: [{ defId: 'food-chocolate', quantity: 1 }] },
        { thresholdPct: 70, guaranteedLoot: [{ defId: 'water-mineral-sealed', quantity: 1 }] },
        { thresholdPct: 100, guaranteedLoot: [{ defId: 'book-swimming-fundamentals', quantity: 1 }] },
      ],
    },
  },
  'old-market-hall': {
    id: 'old-market-hall',
    region: 'den-bosch',
    name: lt('Oude markthal', 'Old market hall'),
    blurb: lt(
      'De markthal is leeg, maar de echo herinnert zich alles.',
      'The market hall is empty, but the echo remembers.',
    ),
    durationMs: 3 * 60 * 1000,
    injuryLevel: { minDurationMs: 5 * 60 * 1000, maxDurationMs: 10 * 60 * 1000 },
    cost: { hunger: 65, thirst: 65, energy: 20 },
    rolls: { min: 3, max: 5 },
    loot: [
      { itemId: 'food-stamppot', weight: 4, min: 1, max: 2 },
      { itemId: 'food-canned-meatballs', weight: 3, min: 1, max: 2 },
      { itemId: 'water-mineral-sealed', weight: 3, min: 1, max: 2 },
      { itemId: 'scrap-plank', weight: 1, min: 1, max: 1 },
      { itemId: 'meds-bandages', weight: 2, min: 1, max: 1 },
      { itemId: 'meds-antibiotics', weight: 1, min: 1, max: 1 },
      { itemId: 'food-chocolate', weight: 2, min: 1, max: 1 },
      { itemId: 'trinket-vinyl', weight: 1, min: 1, max: 1 },
      { itemId: 'trinket-coin', weight: 1, min: 1, max: 1 },
    ],
    diary: diary([
      [
        'Onder de hoge glaskap valt het licht in scherven naar beneden.',
        'Under the tall glass roof light falls in shards.',
      ],
      [
        'Een marktkraam staat nog overeind. "Kaas — €8,50." De kazen liggen er nog.',
        'A stall still stands. “Cheese — €8.50.” The cheeses remain.',
      ],
      [
        'Iemand had een feestje. Lege flessen, een omgevallen taart.',
        'Someone partied—empty bottles, a fallen cake.',
      ],
      [
        'In een hoek vond ik een doos antibiotica. Onaangebroken. Goud.',
        'In a corner, antibiotics—unopened. Gold.',
      ],
      [
        'Iemand fluit een wijsje een straat verderop. Ik wacht tot het stopt.',
        'Someone whistles a tune a street away. I wait until it stops.',
      ],
    ]),
    unlockRequirement: {
      locationId: 'fashion-stores',
      explorationPct: LOCATION_UNLOCK_EXPLORATION_PCT,
    },
    exploration: {
      progressGain: { min: 7, max: 11 },
      lootRevealAtPct: LOOT_REVEAL_AT_PCT,
      tierRewards: [
        { thresholdPct: 20, guaranteedLoot: [{ defId: 'food-stamppot', quantity: 1 }] },
        { thresholdPct: 50, guaranteedLoot: [{ defId: 'meds-antibiotics', quantity: 1 }] },
        { thresholdPct: 70, guaranteedLoot: [{ defId: 'water-mineral-sealed', quantity: 1 }] },
        { thresholdPct: 100, guaranteedLoot: [{ defId: 'trinket-coin', quantity: 1 }] },
      ],
    },
  },
  'a2-highway-road': {
    id: 'a2-highway-road',
    region: 'a2-highway',
    name: lt('A2 — wrakkenstrook', 'A2 — wreck lane'),
    blurb: lt(
      'Verbrande auto’s en open pleister — een rit naar het noorden.',
      'Burned-out chassis and gap asphalt — a northbound crawl.',
    ),
    durationMs: 120 * 1000,
    injuryLevel: { minDurationMs: 2 * 60 * 1000, maxDurationMs: 6 * 60 * 1000 },
    cost: { hunger: 45, thirst: 40, energy: 12 },
    rolls: { min: 2, max: 4 },
    loot: [
      { itemId: 'scrap-rusty', weight: 6, min: 1, max: 4 },
      { itemId: 'scrap-wire', weight: 5, min: 1, max: 3 },
      { itemId: 'scrap-plank', weight: 3, min: 1, max: 2 },
      { itemId: 'food-crackers', weight: 2, min: 1, max: 2 },
      { itemId: 'water-bottle-half', weight: 3, min: 1, max: 2 },
      { itemId: 'book-driving-theory', weight: 1, min: 1, max: 1 },
      { itemId: 'trinket-lighter', weight: 2, min: 1, max: 1 },
    ],
    diary: diary([
      [
        'De berm staat vol glasscherven. Bandensporen verdwijnen in modder.',
        'The shoulder glitters with glass. Tire marks vanish into mud.',
      ],
      [
        'Een tankstationbord wijst naar Utrecht. Het metaal is omgebogen.',
        'A fuel-station sign points toward Utrecht—the metal’s bent.',
      ],
      [
        'Ik rook olie en warm plastic. Geen motor die nog leeft.',
        'Oil and hot plastic—no engine left alive.',
      ],
      [
        'Tussen twee vrachtwagens lag een kinderstoel. Leeg.',
        'Between two lorries a child seat. Empty.',
      ],
      [
        'Ravens krassen op het dak van een touringcar. Ze verdedigen een bot.',
        'Ravens scrape the coach roof—they guard a bone.',
      ],
    ]),
    exploration: {
      progressGain: { min: 13, max: 18 },
      lootRevealAtPct: LOOT_REVEAL_AT_PCT,
      tierRewards: [
        { thresholdPct: 20, guaranteedLoot: [{ defId: 'scrap-wire', quantity: 2 }] },
        { thresholdPct: 50, guaranteedLoot: [{ defId: 'scrap-rusty', quantity: 3 }] },
        { thresholdPct: 70, guaranteedLoot: [{ defId: 'scrap-plank', quantity: 2 }] },
        { thresholdPct: 100, guaranteedLoot: [{ defId: 'book-bartering-highway', quantity: 1 }] },
      ],
    },
  },
  'a2-rest-stop-collapsed': {
    id: 'a2-rest-stop-collapsed',
    region: 'a2-highway',
    name: lt('Ingestorte rustplaats', 'Collapsed rest stop'),
    blurb: lt(
      'Tankshop, toiletten en dak dat naar beneden kwam.',
      'Fuel court, toilets, and a roof that finally gave in.',
    ),
    durationMs: 165 * 1000,
    injuryLevel: { minDurationMs: 4 * 60 * 1000, maxDurationMs: 10 * 60 * 1000 },
    cost: { hunger: 55, thirst: 50, energy: 18 },
    rolls: { min: 2, max: 5 },
    loot: [
      { itemId: 'food-crackers', weight: 4, min: 1, max: 3 },
      { itemId: 'meds-paracetamol', weight: 4, min: 1, max: 2 },
      { itemId: 'scrap-electronics', weight: 4, min: 1, max: 2 },
      { itemId: 'scrap-rusty', weight: 3, min: 1, max: 3 },
      { itemId: 'trinket-coin', weight: 2, min: 1, max: 1 },
      { itemId: 'water-rain-jerrycan', weight: 3, min: 1, max: 1 },
      { itemId: 'scrap-radio-broken', weight: 3, min: 1, max: 1 },
      { itemId: 'book-field-first-aid', weight: 1, min: 1, max: 1 },
    ],
    diary: diary([
      [
        'De deur van het toilet hing scheef. Nog steeds half water in de bak.',
        'The toilet door hung crooked—still half a tank of water.',
      ],
      [
        'Achter de snackbar lag een krat warme frisdrankflacons. Leeg gezogen.',
        'Behind the snack bar a crate of soda bottles—sucked dry.',
      ],
      [
        'Op een tafeltje een ansichtkaart: Domtoren. Iemand wilde naar Utrecht.',
        'A postcard on a table: the Dom. Someone meant to reach Utrecht.',
      ],
      [
        'Het dak ligt op de parkeerplaats. Ik klauter erlangs.',
        'The roof sits on the parking lane—I climb around it.',
      ],
      [
        'In een auto zat nog een kinderbooster. Ik liet hem liggen.',
        'A booster seat remained—I left it.',
      ],
    ]),
    unlockRequirement: {
      locationId: 'a2-highway-road',
      explorationPct: LOCATION_UNLOCK_EXPLORATION_PCT,
    },
    exploration: {
      progressGain: { min: 9, max: 14 },
      lootRevealAtPct: LOOT_REVEAL_AT_PCT,
      tierRewards: [
        { thresholdPct: 20, guaranteedLoot: [{ defId: 'food-crackers', quantity: 2 }] },
        { thresholdPct: 50, guaranteedLoot: [{ defId: 'meds-paracetamol', quantity: 1 }] },
        {
          thresholdPct: 70,
          guaranteedLoot: [{ defId: 'scrap-radio-broken', quantity: 1 }],
        },
        { thresholdPct: 100, guaranteedLoot: [{ defId: 'scrap-electronics', quantity: 2 }] },
      ],
    },
  },
  'utrecht-domplein': {
    id: 'utrecht-domplein',
    region: 'utrecht',
    name: lt('Domplein', 'Dom square'),
    blurb: lt(
      'Toren en plein — steil naar boven, diep onder je voeten.',
      'Tower and square—steep climbs and hollow stone below.',
    ),
    durationMs: 200 * 1000,
    injuryLevel: { minDurationMs: 5 * 60 * 1000, maxDurationMs: 11 * 60 * 1000 },
    cost: { hunger: 70, thirst: 55, energy: 22 },
    rolls: { min: 3, max: 5 },
    loot: [
      { itemId: 'scrap-rusty', weight: 4, min: 1, max: 3 },
      { itemId: 'trinket-coin', weight: 3, min: 1, max: 2 },
      { itemId: 'food-stamppot', weight: 3, min: 1, max: 2 },
      { itemId: 'water-mineral-sealed', weight: 2, min: 1, max: 1 },
      { itemId: 'scrap-electronics', weight: 3, min: 1, max: 2 },
      { itemId: 'book-hacking-utrecht', weight: 1, min: 1, max: 1 },
      { itemId: 'item-pet-treat', weight: 2, min: 1, max: 2 },
    ],
    diary: diary([
      [
        'Klokkengeluid is weg. Alleen kraaien op het dak.',
        'No bells—only crows on the roofline.',
      ],
      [
        'Een trap naar de toren is ingestort. Ik nam een andere richel.',
        'The tower stair collapsed—I took another ledge.',
      ],
      [
        'Onder een plaquette las ik een naam die ik kende uit geschiedenisles.',
        'Under a plaque a name from history class.',
      ],
      [
        'De wind trekt door de arcade als door een fluit.',
        'Wind pulls through the arcade like a flute.',
      ],
      [
        'Iemand had kaarsvet op de steen gesmolten. Nog warm?',
        'Someone spilled wax on the stone—still warm?',
      ],
    ]),
    exploration: {
      progressGain: { min: 8, max: 12 },
      lootRevealAtPct: LOOT_REVEAL_AT_PCT,
      tierRewards: [
        { thresholdPct: 20, guaranteedLoot: [{ defId: 'scrap-rusty', quantity: 2 }] },
        { thresholdPct: 50, guaranteedLoot: [{ defId: 'trinket-coin', quantity: 1 }] },
        { thresholdPct: 70, guaranteedLoot: [{ defId: 'scrap-electronics', quantity: 1 }] },
        { thresholdPct: 100, guaranteedLoot: [{ defId: 'book-hacking-utrecht', quantity: 1 }] },
      ],
      recruit: {
        atPct: 45,
        survivor: LOTTE_TRADER_RECRUIT,
      },
    },
  },
  'utrecht-oudegracht': {
    id: 'utrecht-oudegracht',
    region: 'utrecht',
    name: lt('Oudegracht werfkelders', 'Oudegracht wharf cellars'),
    blurb: lt(
      'Water onder je voeten, luiken dicht. Goede plek voor verborgen spullen.',
      'Water at your feet, shutters tight—a place for hidden hauls.',
    ),
    durationMs: 185 * 1000,
    injuryLevel: { minDurationMs: 4 * 60 * 1000, maxDurationMs: 9 * 60 * 1000 },
    cost: { hunger: 60, thirst: 45, energy: 18 },
    rolls: { min: 2, max: 4 },
    loot: [
      { itemId: 'food-canned-meatballs', weight: 4, min: 1, max: 2 },
      { itemId: 'water-rain-jerrycan', weight: 4, min: 1, max: 2 },
      { itemId: 'trinket-watch', weight: 2, min: 1, max: 1 },
      { itemId: 'scrap-plank', weight: 3, min: 1, max: 2 },
      { itemId: 'meds-bandages', weight: 2, min: 1, max: 1 },
      { itemId: 'trinket-vinyl', weight: 1, min: 1, max: 1 },
    ],
    diary: diary([
      [
        'Een werfkelder stond open. Rook naar bier en schimmel.',
        'A cellar stood open—beer and mould.',
      ],
      [
        'Een boot lag half gezonken. Goed voor planken.',
        'A boat half sunk—good for planks.',
      ],
      [
        'Kinderhandschrift op een luik: "niet naar binnen".',
        'Child writing on a hatch: “don’t go in”.',
      ],
      [
        'Ik gleed bijna van een gladde trede. Het water nam het geluid.',
        'Nearly slipped on slick stone—the water swallowed sound.',
      ],
      [
        'Iemand had een ketting aan een ring bevestigd. Leeg slot.',
        'A chain on a ring—empty lock.',
      ],
    ]),
    unlockRequirement: {
      locationId: 'utrecht-domplein',
      explorationPct: LOCATION_UNLOCK_EXPLORATION_PCT,
    },
    exploration: {
      progressGain: { min: 8, max: 13 },
      lootRevealAtPct: LOOT_REVEAL_AT_PCT,
      tierRewards: [
        { thresholdPct: 20, guaranteedLoot: [{ defId: 'food-canned-meatballs', quantity: 1 }] },
        { thresholdPct: 50, guaranteedLoot: [{ defId: 'water-rain-jerrycan', quantity: 1 }] },
        { thresholdPct: 70, guaranteedLoot: [{ defId: 'meds-bandages', quantity: 1 }] },
        { thresholdPct: 100, guaranteedLoot: [{ defId: 'trinket-watch', quantity: 1 }] },
      ],
    },
  },
  'utrecht-centraal': {
    id: 'utrecht-centraal',
    region: 'utrecht',
    name: lt('Utrecht Centraal — hal', 'Utrecht Centraal — hall'),
    blurb: lt(
      'Bagagebanden stil. Elektronica en verloren koffers.',
      'Still baggage belts—electronics and lost luggage.',
    ),
    durationMs: 210 * 1000,
    injuryLevel: { minDurationMs: 4 * 60 * 1000, maxDurationMs: 10 * 60 * 1000 },
    cost: { hunger: 65, thirst: 55, energy: 20 },
    rolls: { min: 3, max: 5 },
    loot: [
      { itemId: 'scrap-electronics', weight: 6, min: 1, max: 3 },
      { itemId: 'laptop-salvage', weight: 3, min: 1, max: 1 },
      { itemId: 'monitor-salvage', weight: 2, min: 1, max: 1 },
      { itemId: 'water-bottle-half', weight: 3, min: 1, max: 3 },
      { itemId: 'trinket-plush', weight: 2, min: 1, max: 1 },
      { itemId: 'scrap-wire', weight: 3, min: 1, max: 2 },
      { itemId: 'food-crackers', weight: 2, min: 1, max: 2 },
    ],
    diary: diary([
      [
        'De grote klok op het perron staat stil op 03:11.',
        'The platform clock frozen at 03:11.',
      ],
      [
        'Bagageband N vol met natte jassen. Niemand claimt ze.',
        'Carousel N full of wet coats—no claim tags.',
      ],
      [
        'In een kiosk lag nog een rol tickets. Ongebruikt.',
        'A kiosk still held a roll of tickets—unused.',
      ],
      [
        'Een speaker kraakte en sprak een naam. Verkeerde taal.',
        'A speaker crackled a name—wrong language.',
      ],
      [
        'Ik vond een telefoon met 3% batterij. Geen bereik.',
        'A phone at 3%—no signal.',
      ],
    ]),
    unlockRequirement: {
      locationId: 'utrecht-oudegracht',
      explorationPct: LOCATION_UNLOCK_EXPLORATION_PCT,
    },
    exploration: {
      progressGain: { min: 7, max: 11 },
      lootRevealAtPct: LOOT_REVEAL_AT_PCT,
      tierRewards: [
        { thresholdPct: 20, guaranteedLoot: [{ defId: 'scrap-electronics', quantity: 1 }] },
        { thresholdPct: 50, guaranteedLoot: [{ defId: 'laptop-salvage', quantity: 1 }] },
        { thresholdPct: 70, guaranteedLoot: [{ defId: 'scrap-wire', quantity: 2 }] },
        { thresholdPct: 100, guaranteedLoot: [{ defId: 'raspberry-pi-salvage', quantity: 1 }] },
      ],
    },
  },
  'utrecht-jaarbeurs': {
    id: 'utrecht-jaarbeurs',
    region: 'utrecht',
    name: lt('Jaarbeurs hallen', 'Jaarbeurs halls'),
    blurb: lt(
      'Witte hallen vol staanders — industriële schroot en medicijnen.',
      'White halls of booths—industrial scrap and meds.',
    ),
    durationMs: 195 * 1000,
    injuryLevel: { minDurationMs: 5 * 60 * 1000, maxDurationMs: 11 * 60 * 1000 },
    cost: { hunger: 68, thirst: 60, energy: 22 },
    rolls: { min: 3, max: 5 },
    loot: [
      { itemId: 'scrap-rusty', weight: 5, min: 1, max: 4 },
      { itemId: 'meds-bandages', weight: 4, min: 1, max: 2 },
      { itemId: 'scrap-plank', weight: 3, min: 1, max: 3 },
      { itemId: 'computer-tower-salvage', weight: 2, min: 1, max: 1 },
      { itemId: 'food-stamppot', weight: 2, min: 1, max: 2 },
      { itemId: 'scrap-electronics', weight: 3, min: 1, max: 2 },
    ],
    diary: diary([
      [
        'Rijen met verlaten kraampjes. Een bonprinter spuwt nog leeg papier.',
        'Rows of stalls—a receipt printer spits blank paper.',
      ],
      [
        'Iemand bouwde een schuilhoek van beurswandpanelen.',
        'Someone built a nook from booth panels.',
      ],
      [
        'De vloer kraakte als ik over een rol loper stapte.',
        'The floor cracked under a runner roll.',
      ],
      [
        'Een geur van popcorn die al jaren geleden op was.',
        'The ghost smell of popcorn long gone.',
      ],
      [
        'In een hoek lag een statief met een oude beamer. Lens gebarsten.',
        'A tripod with an old projector—lens cracked.',
      ],
    ]),
    unlockRequirement: {
      locationId: 'utrecht-centraal',
      explorationPct: LOCATION_UNLOCK_EXPLORATION_PCT,
    },
    exploration: {
      progressGain: { min: 7, max: 12 },
      lootRevealAtPct: LOOT_REVEAL_AT_PCT,
      tierRewards: [
        { thresholdPct: 20, guaranteedLoot: [{ defId: 'scrap-rusty', quantity: 3 }] },
        { thresholdPct: 50, guaranteedLoot: [{ defId: 'meds-bandages', quantity: 2 }] },
        { thresholdPct: 70, guaranteedLoot: [{ defId: 'computer-tower-salvage', quantity: 1 }] },
        { thresholdPct: 100, guaranteedLoot: [{ defId: 'scrap-electronics', quantity: 2 }] },
      ],
    },
  },
  'utrecht-galgenwaard': {
    id: 'utrecht-galgenwaard',
    region: 'utrecht',
    name: lt('Galgenwaard stadion', 'Galgenwaard stadium'),
    blurb: lt(
      'Open tribunes — harde wind en sportgerelateerde schroot.',
      'Open stands—hard wind and sports junk.',
    ),
    durationMs: 175 * 1000,
    injuryLevel: { minDurationMs: 6 * 60 * 1000, maxDurationMs: 12 * 60 * 1000 },
    cost: { hunger: 62, thirst: 65, energy: 24 },
    rolls: { min: 3, max: 5 },
    loot: [
      { itemId: 'weapon-tennis-racket', weight: 4, min: 1, max: 1 },
      { itemId: 'weapon-plank', weight: 3, min: 1, max: 1 },
      { itemId: 'trinket-soccer-ball', weight: 4, min: 1, max: 2 },
      { itemId: 'food-chocolate', weight: 3, min: 1, max: 2 },
      { itemId: 'water-bottle-half', weight: 3, min: 1, max: 3 },
      { itemId: 'scrap-rusty', weight: 3, min: 1, max: 3 },
    ],
    diary: diary([
      [
        'Het veld was bruin en kaal. Geen fluitsignalen meer.',
        'The pitch brown and bare—no whistles.',
      ],
      [
        'Een shirt hing aan een hek. Naam op de rug verbleekt.',
        'A shirt on the fence—name faded on the back.',
      ],
      [
        'Ik hoorde metaal rinkelen in de wind: rijen zitjes los.',
        'Metal rattled—rows of seats coming loose.',
      ],
      [
        'Op een snackbar lag nog saus in tubes. Beschimmeld.',
        'Concession sauces in tubes—mould lines.',
      ],
      [
        'Kraaien zaten op het doelnet als jury.',
        'Crows on the goal net like judges.',
      ],
    ]),
    unlockRequirement: {
      locationId: 'utrecht-jaarbeurs',
      explorationPct: LOCATION_UNLOCK_EXPLORATION_PCT,
    },
    exploration: {
      progressGain: { min: 9, max: 14 },
      lootRevealAtPct: LOOT_REVEAL_AT_PCT,
      tierRewards: [
        { thresholdPct: 20, guaranteedLoot: [{ defId: 'weapon-tennis-racket', quantity: 1 }] },
        { thresholdPct: 50, guaranteedLoot: [{ defId: 'trinket-soccer-ball', quantity: 1 }] },
        { thresholdPct: 70, guaranteedLoot: [{ defId: 'food-chocolate', quantity: 1 }] },
        { thresholdPct: 100, guaranteedLoot: [{ defId: 'weapon-plank', quantity: 1 }] },
      ],
    },
  },
  'utrecht-umc-fringe': {
    id: 'utrecht-umc-fringe',
    region: 'utrecht',
    name: lt('UMC — rand Science Park', 'UMC — Science Park fringe'),
    blurb: lt(
      'Ziekenhuisrand en labs — medicijnen en dikke boeken.',
      'Hospital fringe and labs—meds and dense texts.',
    ),
    durationMs: 205 * 1000,
    injuryLevel: { minDurationMs: 4 * 60 * 1000, maxDurationMs: 10 * 60 * 1000 },
    cost: { hunger: 58, thirst: 55, energy: 16 },
    rolls: { min: 2, max: 4 },
    loot: [
      { itemId: 'meds-antibiotics', weight: 3, min: 1, max: 1 },
      { itemId: 'meds-bandages', weight: 5, min: 1, max: 3 },
      { itemId: 'meds-paracetamol', weight: 4, min: 1, max: 2 },
      { itemId: 'book-field-first-aid', weight: 2, min: 1, max: 1 },
      { itemId: 'scrap-electronics', weight: 3, min: 1, max: 2 },
      { itemId: 'food-bread', weight: 2, min: 1, max: 2 },
    ],
    diary: diary([
      [
        'Een rolstoel stond op de helling. Rem nog aan.',
        'A wheelchair on the slope—brake still on.',
      ],
      [
        'Laboratoriumglas lag in een bak als breukwerk.',
        'Lab glass in a bin like pottery shards.',
      ],
      [
        'Geurstrips tegen desinfectie deden niets meer tegen de geur van schimmel.',
        'Scent strips couldn’t beat mildew.',
      ],
      [
        'Op een whiteboard stond een vergelijking half weggeveegd.',
        'Half-erased equation on a whiteboard.',
      ],
      [
        'Ik vond een stapel dikke naslagsels. Te zwaar om alles mee te nemen.',
        'A stack of heavy texts—too much to carry.',
      ],
    ]),
    unlockRequirement: {
      locationId: 'utrecht-galgenwaard',
      explorationPct: LOCATION_UNLOCK_EXPLORATION_PCT,
    },
    exploration: {
      progressGain: { min: 7, max: 11 },
      lootRevealAtPct: LOOT_REVEAL_AT_PCT,
      tierRewards: [
        { thresholdPct: 20, guaranteedLoot: [{ defId: 'meds-bandages', quantity: 2 }] },
        { thresholdPct: 50, guaranteedLoot: [{ defId: 'meds-paracetamol', quantity: 2 }] },
        { thresholdPct: 70, guaranteedLoot: [{ defId: 'meds-antibiotics', quantity: 1 }] },
        { thresholdPct: 100, guaranteedLoot: [{ defId: 'book-field-first-aid', quantity: 1 }] },
      ],
    },
  },
  'utrecht-lombok': {
    id: 'utrecht-lombok',
    region: 'utrecht',
    name: lt('Lombok buurt', 'Lombok district'),
    blurb: lt(
      'Kruiden, rijst en stemmen uit alle windstreken — nog wat diversiteit.',
      'Spices, rice, and voices from everywhere—some diversity left.',
    ),
    durationMs: 170 * 1000,
    injuryLevel: { minDurationMs: 3 * 60 * 1000, maxDurationMs: 8 * 60 * 1000 },
    cost: { hunger: 52, thirst: 48, energy: 14 },
    rolls: { min: 2, max: 4 },
    loot: [
      { itemId: 'food-stamppot', weight: 4, min: 1, max: 2 },
      { itemId: 'food-canned-meatballs', weight: 4, min: 1, max: 2 },
      { itemId: 'food-chocolate', weight: 3, min: 1, max: 2 },
      { itemId: 'water-mineral-sealed', weight: 2, min: 1, max: 1 },
      { itemId: 'trinket-lighter', weight: 2, min: 1, max: 1 },
      { itemId: 'scrap-rusty', weight: 2, min: 1, max: 2 },
    ],
    diary: diary([
      [
        'Een etalage vol specerijpotjes. Sommige gebarsten, nog steeds geurig.',
        'Spice jars—some cracked, still fragrant.',
      ],
      [
        'Twee talen op hetzelfde briefje: "open".',
        'Two languages on the same note: “open”.',
      ],
      [
        'Een fietskar lag op zijn kant. Groente gemummificeerd.',
        'A cargo bike on its side—mummified greens.',
      ],
      [
        'Kinderkrijt op stoep: een huis met zon.',
        'Sidewalk chalk—a house with a sun.',
      ],
      [
        'Ik rook bakolie die te lang op het vuur heeft gestaan.',
        'Oil that stayed on the heat too long.',
      ],
    ]),
    unlockRequirement: {
      locationId: 'utrecht-umc-fringe',
      explorationPct: LOCATION_UNLOCK_EXPLORATION_PCT,
    },
    exploration: {
      progressGain: { min: 9, max: 13 },
      lootRevealAtPct: LOOT_REVEAL_AT_PCT,
      tierRewards: [
        { thresholdPct: 20, guaranteedLoot: [{ defId: 'food-stamppot', quantity: 1 }] },
        { thresholdPct: 50, guaranteedLoot: [{ defId: 'food-canned-meatballs', quantity: 2 }] },
        { thresholdPct: 70, guaranteedLoot: [{ defId: 'water-mineral-sealed', quantity: 1 }] },
        { thresholdPct: 100, guaranteedLoot: [{ defId: 'food-chocolate', quantity: 2 }] },
      ],
    },
  },
  'utrecht-hoog-catharijne': {
    id: 'utrecht-hoog-catharijne',
    region: 'utrecht',
    name: lt('Hoog Catharijne ruïnes', 'Hoog Catharijne ruins'),
    blurb: lt(
      'Mallgangen ingestort — supermarktplus loot onder beton.',
      'Collapsed mall corridors—supermarket-tier haul under concrete.',
    ),
    durationMs: 220 * 1000,
    injuryLevel: { minDurationMs: 5 * 60 * 1000, maxDurationMs: 12 * 60 * 1000 },
    cost: { hunger: 72, thirst: 70, energy: 26 },
    rolls: { min: 4, max: 6 },
    loot: [
      { itemId: 'food-crackers', weight: 5, min: 2, max: 5 },
      { itemId: 'food-bread', weight: 4, min: 1, max: 3 },
      { itemId: 'water-rain-jerrycan', weight: 4, min: 1, max: 2 },
      { itemId: 'food-chocolate', weight: 3, min: 1, max: 2 },
      { itemId: 'scrap-rusty', weight: 4, min: 1, max: 4 },
      { itemId: 'trinket-plush', weight: 2, min: 1, max: 1 },
      { itemId: 'meds-paracetamol', weight: 3, min: 1, max: 2 },
    ],
    diary: diary([
      [
        'Het plafond van de hoofdgalerij hing als een verzonken schip.',
        'The main gallery ceiling hung like a sunk hull.',
      ],
      [
        'Roltrappen als gekanteelde tanden naar beneden.',
        'Escalators like broken teeth.',
      ],
      [
        'In een supermarkt hoek vond ik nog verzegelde crackers.',
        'Corner supermarket—still sealed crackers.',
      ],
      [
        'Een muziekje speelde uit een reclamezuil zonder stroom. Hallucinatie?',
        'Music from a kiosk without power—hallucination?',
      ],
      [
        'Ratten liepen alsof ze eigenaar waren van het diepvriesvak.',
        'Rats walked like they owned the freezer aisle.',
      ],
    ]),
    unlockRequirement: {
      locationId: 'utrecht-lombok',
      explorationPct: LOCATION_UNLOCK_EXPLORATION_PCT,
    },
    exploration: {
      progressGain: { min: 6, max: 10 },
      lootRevealAtPct: LOOT_REVEAL_AT_PCT,
      tierRewards: [
        { thresholdPct: 20, guaranteedLoot: [{ defId: 'food-crackers', quantity: 3 }] },
        { thresholdPct: 50, guaranteedLoot: [{ defId: 'water-rain-jerrycan', quantity: 2 }] },
        { thresholdPct: 70, guaranteedLoot: [{ defId: 'food-bread', quantity: 2 }] },
        { thresholdPct: 100, guaranteedLoot: [{ defId: 'meds-paracetamol', quantity: 2 }] },
      ],
    },
  },
  'utrecht-nieuwegracht': {
    id: 'utrecht-nieuwegracht',
    region: 'utrecht',
    name: lt('Nieuwegracht steeg', 'Nieuwegracht alley'),
    blurb: lt(
      'Smalle gracht en bruggetjes — stille route met verrassingen.',
      'Narrow canal and footbridges—a quiet route with surprises.',
    ),
    durationMs: 190 * 1000,
    injuryLevel: { minDurationMs: 4 * 60 * 1000, maxDurationMs: 9 * 60 * 1000 },
    cost: { hunger: 58, thirst: 52, energy: 17 },
    rolls: { min: 2, max: 4 },
    loot: [
      { itemId: 'trinket-watch', weight: 3, min: 1, max: 1 },
      { itemId: 'trinket-coin', weight: 3, min: 1, max: 1 },
      { itemId: 'water-mineral-sealed', weight: 2, min: 1, max: 1 },
      { itemId: 'scrap-wire', weight: 4, min: 1, max: 3 },
      { itemId: 'food-chocolate', weight: 2, min: 1, max: 1 },
      { itemId: 'item-pet-treat', weight: 3, min: 1, max: 2 },
      { itemId: 'pet-companion-stray', weight: 1, min: 1, max: 1 },
    ],
    diary: diary([
      [
        'De steeg was zo smal dat ik de muur voelde met mijn ellebogen.',
        'The alley so narrow my elbows brushed stone.',
      ],
      [
        'Een boot lag vast in het slib. Geen golf meer.',
        'A boat stuck in silt—no wake.',
      ],
      [
        'Iemand had bloempotten op het water laten drijven als vlotjes.',
        'Flowerpots floated like tiny rafts.',
      ],
      [
        'Kat keek vanaf een daklijntje. Geen miauwen.',
        'A cat on a gutter—silent.',
      ],
      [
        'Ik vond een ansichtkaart van voor de ramp. Zelfde gracht, andere kleur.',
        'A postcard from before—same canal, different colour.',
      ],
    ]),
    unlockRequirement: {
      locationId: 'utrecht-hoog-catharijne',
      explorationPct: LOCATION_UNLOCK_EXPLORATION_PCT,
    },
    exploration: {
      progressGain: { min: 8, max: 13 },
      lootRevealAtPct: LOOT_REVEAL_AT_PCT,
      tierRewards: [
        { thresholdPct: 20, guaranteedLoot: [{ defId: 'scrap-wire', quantity: 2 }] },
        { thresholdPct: 50, guaranteedLoot: [{ defId: 'trinket-watch', quantity: 1 }] },
        { thresholdPct: 70, guaranteedLoot: [{ defId: 'water-mineral-sealed', quantity: 1 }] },
        {
          thresholdPct: 100,
          guaranteedLoot: [{ defId: 'pet-companion-stray', quantity: 1 }],
        },
      ],
    },
  },
}

export const LOCATION_LIST: Location[] = Object.values(LOCATIONS)

export function createInitialLocationExplorationState(): Record<
  LocationId,
  LocationExplorationState
> {
  const out = {} as Record<LocationId, LocationExplorationState>
  for (const location of LOCATION_LIST) {
    out[location.id] = {
      progressPct: 0,
      claimedRewardThresholds: [],
      recruitOffered: false,
    }
  }
  return out
}

export function getLocationExplorationPct(
  state: Partial<Record<LocationId, LocationExplorationState>> | undefined,
  locationId: LocationId,
): number {
  return Math.max(0, Math.min(100, Math.round(state?.[locationId]?.progressPct ?? 0)))
}

/** First Den Bosch expedition spot — gated by completing the gateway quest (plus prerequisite exploration chain). */
export const DEN_BOSCH_FIRST_LOCATION_ID: LocationId = 'verkade-factory'

/** Four inner-city Den Bosch sites used for “Den Bosch complete” (100% exploration each). */
export const DEN_BOSCH_SITE_LOCATION_IDS: LocationId[] = [
  'verkade-factory',
  'town-hall-offices',
  'fashion-stores',
  'old-market-hall',
]

/** All four inner-city Den Bosch sites cleared (rounded %) — unlocks Utrecht gateway availability */
export function isDenBoschFullyExplored(
  explorationState: Partial<Record<LocationId, LocationExplorationState>> | undefined,
): boolean {
  return DEN_BOSCH_SITE_LOCATION_IDS.every(
    (id) => getLocationExplorationPct(explorationState, id) >= 100,
  )
}

/** First Utrecht pin after Utrecht gateway quest completes */
export const UTRECHT_FIRST_LOCATION_ID: LocationId = 'utrecht-domplein'

export function isLocationUnlocked(
  locationId: LocationId,
  progress: number,
  explorationState: Partial<Record<LocationId, LocationExplorationState>> | undefined,
  quests?: QuestRuntimeState,
): boolean {
  const location = LOCATIONS[locationId]
  if (progress < REGIONS[location.region].unlockAt) return false

  const utrechtGw = quests?.utrechtGateway?.status
  const a2TripsOpen =
    utrechtGw === 'active' || utrechtGw === 'completed'

  if (locationId === 'a2-highway-road') {
    return a2TripsOpen
  }
  if (locationId === 'a2-rest-stop-collapsed') {
    return (
      a2TripsOpen &&
      getLocationExplorationPct(explorationState, 'a2-highway-road') >=
        LOCATION_UNLOCK_EXPLORATION_PCT
    )
  }

  if (location.region === 'utrecht') {
    if (locationId === UTRECHT_FIRST_LOCATION_ID) {
      return utrechtGw === 'completed'
    }
  }

  if (
    locationId === DEN_BOSCH_FIRST_LOCATION_ID &&
    quests?.denBoschGateway.status !== 'completed'
  ) {
    return false
  }
  const req = location.unlockRequirement
  if (!req) return true
  return getLocationExplorationPct(explorationState, req.locationId) >= req.explorationPct
}

/** Ordered participant ids (legacy expeditions only had `survivorId`). */
export function expeditionParticipantIds(exp: Expedition): string[] {
  if (exp.survivorIds?.length) return exp.survivorIds
  return [exp.survivorId]
}

const explorationGainRandInt = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min + 1)) + min

/**
 * One trip's exploration % gain for a team of size `teamSize`.
 * Each member rolls `progressGain` independently with diminishing contribution:
 * member #1 contributes 100%, #2 contributes 50%, #3 contributes 25%, etc.
 * Total added to location progress is `sum(roll_i / 2^i)` where i starts at 0.
 */
export function rollTeamExplorationDeltaPct(
  progressGain: { min: number; max: number },
  teamSize: number,
): number {
  if (teamSize < 1) return 0
  let sum = 0
  for (let i = 0; i < teamSize; i++) {
    const roll = explorationGainRandInt(progressGain.min, progressGain.max)
    sum += roll / (1 + i * 0.34)
  }
  return sum
}
